import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Container,
  Row,
  Col,
  Alert,
  Spinner,
  Modal,
  Form,
  Button,
} from "react-bootstrap";
import HeaderTwo from "../../components/HeaderTwo";
import { BreadcrumbBox } from "../../components/common/Breadcrumb";
import FooterTwo from "../../components/FooterTwo";
import { Styles, StatusTypeModalStyles } from "./styles/adminStatusTypes.js";
import {
  ADMIN_STATUS_TYPES_ENDPOINT,
  ADMIN_STATUS_TYPES_UPDATE_ENDPOINT,
} from "../../config/api";
import { useAuth } from "../../context/AuthContext";

const AdminStatusTypes = () => {
  const { user, loading: authLoading } = useAuth();

  const [statusTypes, setStatusTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 1,
  });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [modalState, setModalState] = useState({
    show: false,
    mode: "add",
    target: null,
    form: {
      name: "",
      description: "",
    },
    saving: false,
    error: "",
  });
  const [deletingId, setDeletingId] = useState("");

  const isAdmin = useMemo(() => {
    if (!Array.isArray(user?.roles)) {
      return false;
    }
    return user.roles.some(
      (role) => typeof role === "string" && role.toLowerCase() === "admin"
    );
  }, [user]);

  const fetchStatusTypes = useCallback(async () => {
    if (!isAdmin) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const params = new URLSearchParams({
        page: String(page),
        limit: String(pageSize),
      });
      if (searchTerm.trim()) {
        params.set("search", searchTerm.trim());
      }

      const response = await fetch(
        `${ADMIN_STATUS_TYPES_ENDPOINT}?${params.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || "Unable to load statuses.");
      }

      const payload = await response.json();
      const statuses = Array.isArray(payload.statuses)
        ? payload.statuses
        : [];
      setStatusTypes(statuses);

      if (payload.pagination) {
        const nextPage = payload.pagination.page || page;
        setPagination({
          page: nextPage,
          limit: payload.pagination.limit || pageSize,
          totalItems: payload.pagination.totalItems || 0,
          totalPages: payload.pagination.totalPages || 1,
        });
        if (nextPage !== page) {
          setPage(nextPage);
        }
      } else {
        setPagination({
          page,
          limit: pageSize,
          totalItems: statuses.length,
          totalPages: 1,
        });
      }
    } catch (err) {
      setError(err.message || "Unable to load statuses.");
      setStatusTypes([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, page, pageSize, searchTerm]);

  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchStatusTypes();
    }
  }, [authLoading, isAdmin, fetchStatusTypes]);

  useEffect(() => {
    setPage((prev) => (prev === 1 ? prev : 1));
  }, [searchTerm]);

  const tableRows = useMemo(
    () =>
      statusTypes.map((status) => ({
        id: status.id,
        name: status.name,
        description: status.description || "",
        createdAt: status.createdAt
          ? new Date(status.createdAt).toLocaleString()
          : "",
        raw: status,
      })),
    [statusTypes]
  );

  const openModal = (mode, target = null) => {
    if (mode === "edit" && target) {
      setModalState({
        show: true,
        mode: "edit",
        target,
        form: {
          name: target.name || "",
          description: target.description || "",
        },
        saving: false,
        error: "",
      });
    } else {
      setModalState({
        show: true,
        mode: "add",
        target: null,
        form: {
          name: "",
          description: "",
        },
        saving: false,
        error: "",
      });
    }
  };

  const closeModal = () => {
    setModalState({
      show: false,
      mode: "add",
      target: null,
      form: {
        name: "",
        description: "",
      },
      saving: false,
      error: "",
    });
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setModalState((prev) => ({
      ...prev,
      form: {
        ...prev.form,
        [name]: value,
      },
      error: "",
    }));
  };

  const submitModal = async (event) => {
    event.preventDefault();
    setModalState((prev) => ({ ...prev, saving: true, error: "" }));

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      if (!modalState.form.name.trim()) {
        throw new Error("Status name is required.");
      }

      const payload = {
        name: modalState.form.name.trim(),
        description: modalState.form.description.trim(),
      };

      const config = {
        method: modalState.mode === "edit" ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      };

      const endpoint =
        modalState.mode === "edit" && modalState.target?.id
          ? ADMIN_STATUS_TYPES_UPDATE_ENDPOINT(modalState.target.id)
          : ADMIN_STATUS_TYPES_ENDPOINT;

      const response = await fetch(endpoint, config);
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result.message || "Unable to save status.");
      }

      closeModal();
      fetchStatusTypes();
    } catch (err) {
      setModalState((prev) => ({
        ...prev,
        error: err.message || "Unable to save status.",
      }));
    } finally {
      setModalState((prev) => ({ ...prev, saving: false }));
    }
  };

  const deleteStatus = async (id) => {
    if (!window.confirm("Delete this application status?")) {
      return;
    }

    setDeletingId(id);

    try {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("Authentication required.");
      }

      const response = await fetch(
        ADMIN_STATUS_TYPES_UPDATE_ENDPOINT(id),
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to delete status.");
      }

      fetchStatusTypes();
    } catch (err) {
      setError(err.message || "Unable to delete status.");
    } finally {
      setDeletingId("");
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setSearchTerm(searchInput.trim());
  };

  const handleClearSearch = () => {
    setSearchInput("");
    if (searchTerm) {
      setSearchTerm("");
    }
  };

  return (
    <Styles>
      <StatusTypeModalStyles />
      <div className="main-wrapper admin-status-types-page">
        <HeaderTwo />
        <BreadcrumbBox title="Application Status Types" />

        <section className="status-types-area">
          <Container>
            <Row>
              <Col md="12">
                {!authLoading && !isAdmin && (
                  <Alert variant="danger">
                    You do not have permission to view this page.
                  </Alert>
                )}
                {isAdmin && (
                  <div className="card-box">
                    <div className="actions">
                      <Button onClick={() => openModal("add")}>
                        Add Status
                      </Button>
                      <div className="filters">
                        <form onSubmit={handleSearchSubmit} className="filters-form">
                          <input
                            type="text"
                            placeholder="Search statuses..."
                            value={searchInput}
                            onChange={(event) => setSearchInput(event.target.value)}
                            disabled={loading}
                          />
                          <Button type="submit" variant="success" disabled={loading}>
                            Search
                          </Button>
                          <Button
                            type="button"
                            variant="outline-secondary"
                            onClick={handleClearSearch}
                            disabled={loading || (!searchTerm && !searchInput.trim())}
                          >
                            Clear
                          </Button>
                        </form>
                      </div>
                    </div>

                    {error && (
                      <Alert variant="danger" className="mt-3">
                        {error}
                      </Alert>
                    )}

                    {loading ? (
                      <div className="text-center my-4">
                        <Spinner animation="border" role="status" />
                      </div>
                    ) : (
                      <div className="data-grid">
                        <table>
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Description</th>
                              <th>Created</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tableRows.length === 0 ? (
                              <tr>
                                <td colSpan="4" className="text-center muted">
                                  No status types found.
                                </td>
                              </tr>
                            ) : (
                              tableRows.map((row) => (
                                <tr key={row.id}>
                                  <td>{row.name}</td>
                                  <td>
                                    {row.description || (
                                      <span className="muted">—</span>
                                    )}
                                  </td>
                                  <td>{row.createdAt || <span className="muted">—</span>}</td>
                                  <td>
                                    <div className="action-buttons">
                                      <button
                                        type="button"
                                        onClick={() => openModal("edit", row.raw)}
                                      >
                                        Edit
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => deleteStatus(row.id)}
                                        disabled={deletingId === row.id}
                                      >
                                        {deletingId === row.id
                                          ? "Deleting..."
                                          : "Delete"}
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>

                        {tableRows.length > 0 && (
                          <div className="pagination-controls">
                            <div className="page-info">
                              Page {pagination.page} of {pagination.totalPages} • {" "}
                              {pagination.totalItems} total
                            </div>
                            <div className="page-actions">
                              <Button
                                variant="outline-secondary"
                                onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                                disabled={page <= 1 || loading}
                              >
                                Previous
                              </Button>
                              <Button
                                variant="outline-secondary"
                                onClick={() =>
                                  setPage((prev) =>
                                    Math.min(prev + 1, pagination.totalPages || prev + 1)
                                  )
                                }
                                disabled={
                                  page >= (pagination.totalPages || 1) || loading
                                }
                              >
                                Next
                              </Button>
                              <Form.Control
                                as="select"
                                value={pageSize}
                                onChange={(event) => {
                                  setPageSize(Number(event.target.value));
                                  setPage(1);
                                }}
                                disabled={loading}
                              >
                                {[10, 20, 50, 100].map((size) => (
                                  <option key={size} value={size}>
                                    {size} / page
                                  </option>
                                ))}
                              </Form.Control>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Container>
        </section>

        <FooterTwo />
      </div>

      <Modal
        show={modalState.show}
        onHide={closeModal}
        centered
        dialogClassName="admin-status-type-modal"
      >
        <Form onSubmit={submitModal}>
          <Modal.Header closeButton>
            <Modal.Title>
              {modalState.mode === "add" ? "Add Status" : "Edit Status"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalState.error && (
              <Alert variant="danger">{modalState.error}</Alert>
            )}
            <Form.Group controlId="status_type_name">
              <Form.Label>Status Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={modalState.form.name}
                onChange={handleFormChange}
                disabled={modalState.saving}
                required
              />
            </Form.Group>
            <Form.Group controlId="status_type_description" className="mt-3">
              <Form.Label>Description (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={modalState.form.description}
                onChange={handleFormChange}
                disabled={modalState.saving}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              onClick={closeModal}
              disabled={modalState.saving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={modalState.saving}>
              {modalState.saving ? "Saving..." : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Styles>
  );
};

export default AdminStatusTypes;
